import { createStore, combineReducers, applyMiddleware } from "redux";
import { createLogger } from "redux-logger";
import { routerReducer, routerMiddleware } from "react-router-redux";
import createSagaMiddleware from "redux-saga";
import createHistory from "history/createBrowserHistory";

import bootSessionProbe from "lib/bootSessionProbe";
import configureApi from "configureApi";
import configureHoneybadger from "configureHoneybadger";
import appReducers from "reducers";
import sagas from "sagas";
import gtm from "lib/gtm/settings";
import { featureIsEnabled } from "settings";
import { applyDxaRecordingForPath } from "lib/analytics/dxaRecording";

export default callback => {
  const history = createHistory();

  // CD-8245 (Group 4): pause/resume Medallia DXA session recording based on
  // the current route. Applied once for the initial location (covers a
  // direct load onto /admin, before the Admin view renders) and then on
  // every subsequent navigation via history.listen — both run outside
  // React, ahead of route-component mount, so the pause is always issued
  // before any /admin* content paints. See lib/analytics/dxaRecording.ts
  // for the approved direct-decibelInsight exception this relies on.
  applyDxaRecordingForPath(history.location.pathname);
  history.listen(location => applyDxaRecordingForPath(location.pathname));

  configureHoneybadger();

  const sagaMiddleware = createSagaMiddleware();
  const gtmMiddleware = gtm.getMiddleware();
  const middlewares = [
    sagaMiddleware,
    routerMiddleware(history),
    ...(gtmMiddleware ? [gtmMiddleware] : [])
  ];

  if (process.env.NODE_ENV !== "production" && featureIsEnabled('redux-logger') !== false) {
    middlewares.push(createLogger());
  }

  const gtmReducers = gtm.getReducers();
  const reducers = { ...appReducers, router: routerReducer, ...(gtmReducers || {}) };
  const store = createStore(combineReducers(reducers), applyMiddleware(...middlewares));

  sagaMiddleware.run(sagas);

  // After the store exists: an expired session signs out through it (see lib/expiredSessionRecovery).
  configureApi({ history, dispatch: store.dispatch });

  // CD-8740: resolve the session from the server rather than from localStorage.
  // `index.js` renders inside this callback, so the promise must always settle —
  // see lib/bootSessionProbe. The loading shell in public/index.html covers the
  // window in which this is in flight.
  bootSessionProbe(store).then(() => callback({ store, history }));
};
